/**
 * Copyright 2024 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import {
  clearProductFilterEvents,
  removeDataFromPage,
  setSelectedNullCartridge,
  setSortByOrItemsPerPageFocus,
} from '@/actions/shelf/shelf';
import { useFeatureFlag } from '@/features/globalConfig';
import { useMonetateDecisionFlag } from '@/features/kibo/api/getKiboDecision';
import { useDispatch } from '@/hooks/redux/useDispatch';
import { useGetCustomPriceFilterLabel } from '@/hooks/useGetCustomPriceFilterLabel';
import { useGetFilterAnalytics } from '@/hooks/useGetFilterAnalytics';
import { useGetSessionFiltersList } from '@/hooks/useGetSessionFiltersList';
import { useInteractionCheck } from '@/hooks/useInteractionCheck';
import { useIs24ProductViewEnabled } from '@/hooks/useIs24ProductViewEnabled';
import type { ReduxState } from '@/types';
import { analyticsDynamicPageName } from '@/utils/analytics/legacyUtils/analyticsDynamicPageName';
import { getContentBlockNames } from '@/utils/analytics/legacyUtils/getContentBlockNames';
import { getPageName } from '@/utils/analytics/legacyUtils/getPageName';
import { getProductFinderAnalytics } from '@/utils/analytics/legacyUtils/getProductFinderAnalytics';
import { getSelectedSortingOptionLabel, getShelfAnalytics } from '@/utils/analytics/shelfAnalytics';
import { useAnalytics } from '@/utils/analytics/useAnalytics';
import { usePageSpecificData } from '@/utils/analytics/usePageSpecificData';
import { usePageViewCall } from '@/utils/analytics/usePageViewCall';
import { isBrandShelfPage } from '@/utils/isBrandShelfPage';
import { getPathnameFromAsPath, parseUrl } from '@/utils/urlHelpers';
import { FACETED_URL } from '@/utils/validatorRegex';
import { useRouter } from 'next/router';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useProductList } from '../../context/useProductList';
import usePrevious from '@/utils/usePrevious';
import { getProductStrikethroughAnalyticValue } from '@/components/ProductDetail/analytics/getProductStrikethroughAnalyticValue';
import { type MappedBrandShelfResults, type MappedBrandShelfResultsAces } from '@/features/brands';
import { eventTypes } from '@/constants/event';
import { getContentBlockTypes } from '@/utils/analytics/legacyUtils/getContentBlockTypes';
import { getSeoPageType } from '@/utils/analytics/legacyUtils/getSeoPageType';
import { sessionStorage } from '@/utils/sessionStorage';
import { pageTypes } from '@/utils/analytics/legacyUtils/pageTypes';
import { setupAnalytics } from '@/utils/analytics/config/setupAnalytics';
import root from 'window-or-global';
import { siteSections } from '@/utils/siteSections';
import { useGetShelfPageRecords } from '../../hooks/useGetShelfPageRecords';
import { useYmmeDefaultData } from '@/features/ymme/utils/useYmmeDefaultData';
import { getAnalyticsRelativeComplementOf } from '@/utils/analytics/getAnalyticsRelativeComplementOf';
import { useFilteredSortingOptions } from '@/hooks/useFilteredSortingOptions';
import { useLabels } from '@/hooks/useLabels';
import { useHeaderData } from '@/features/header/api/getHeader';
import { usePreferredVehicle } from '@/features/header/hooks/usePreferredVehicle';
import { resetAnalyticsDatalayer } from '@/utils/analytics/dataLayer/resetAnalyticsDatalayer';
import { useStoreDetailsData } from '@/features/header/api/getStoreDetails';
import { type MappedSearchProductData } from '@/features/search';
import { type ProductShelfACESResponse, type ProductShelfResponse } from '@/features/category';
import { useIsStoreSelected } from '@/features/header/hooks/useIsStoreSelected';
import { shelfPageVariables } from '@/constants/shelfPageVariables';
import { useProductListView } from '../../context/useProductListView';
import { useAcesFlags } from '@/features/ymme/utils/acesFlags';
import {
  type ProductShelfACESResultsModel,
  type ProductShelfResultsModel,
} from '@/api/types/browse-search-types';
import { useReviewStatistics } from '@/features/customReviews/api/getReviewStatistics';
import { formatReviewStatistics } from '../../utils/formatReviewStatistics';

const labelMap = {
  featuredSortingOption: 'label.sort.featured',
};

type ShelfAnalyticsParams = {
  shelfCatLabel: string | undefined;
  isBrandShelf: boolean;
  isBrandShelfDataFetching: boolean;
  isNavigatedFromVisualFilter: boolean;
  isVisualFiltersDisplayedOnShelf: boolean;
  infiniteQueryIsPending: boolean | undefined;
  prevSecondaryProductSkuIsFetching: boolean | undefined;
  brandShelfData: MappedBrandShelfResults | MappedBrandShelfResultsAces;
  searchProductData: MappedSearchProductData;
  productShelfResultsData: ProductShelfResponse | ProductShelfACESResponse | undefined;
};

export const useShelfAnalytics = ({
  shelfCatLabel,
  isBrandShelf,
  isBrandShelfDataFetching,
  isNavigatedFromVisualFilter,
  infiniteQueryIsPending,
  isVisualFiltersDisplayedOnShelf,
  prevSecondaryProductSkuIsFetching,
  brandShelfData,
  searchProductData,
  productShelfResultsData,
}: ShelfAnalyticsParams) => {
  const labels = useLabels(labelMap);
  const router = useRouter();
  const { asPath } = router;
  const { query } = parseUrl(asPath);

  const dispatch = useDispatch();
  const isUtagDefined = useAnalytics();
  const pageViewCall = usePageViewCall();
  const { setPageSpecificData } = usePageSpecificData();

  const selectedFilterValue = useSelector(({ shelf }: ReduxState) => shelf.selectedFilterValue);
  const eventType = useSelector(({ shelf }: ReduxState) => shelf.eventType);
  const selectedNullCartridge = useSelector(({ shelf }: ReduxState) => shelf.selectedNullCartridge);
  const { listViewState } = useProductListView();
  const isList = isBrandShelfPage(router.asPath)
    ? listViewState.isBrandListView
    : listViewState.isListView;

  const dispatchClearProductFilterEvents = () => dispatch(clearProductFilterEvents());
  const dispatchSetSelectedNullCartridge = (value: string) =>
    dispatch(setSelectedNullCartridge(value));
  const dispatchRemoveDataFromPage = () => dispatch(removeDataFromPage());

  const dealDrawerShelfEnabled = useFeatureFlag('DEAL_DRAWER_SHELF_ENABLED') === 'true';
  const strikeThroughPricingEnabled = useFeatureFlag('SHOW_STRIKE_THROUGH_PRICING') === 'true';
  const isLoadMoreEnabled = useMonetateDecisionFlag('loadMoreEnabled');
  const enableVdpDsUniqueDisplay = useMonetateDecisionFlag('enableVdpDsUniqueDisplay');
  const { acesEnabled, acesNonVehicleEndpointsEnabled } = useAcesFlags();
  const isACESEnabled = acesEnabled && acesNonVehicleEndpointsEnabled;

  const { data: headerData } = useHeaderData();
  const preferredVehicle = usePreferredVehicle();

  const { data: storeDetails } = useStoreDetailsData();

  const {
    data: shelfData,
    isSuccess: shelfRequestSuccess,
    isFetching: shelfRequestFetching,
    productSkuIsFetching,
    secondaryProductSkuIsFetching,
    loadMoreClicked,
    additionalProductSkuDetailsIsFetching,
  } = useProductList();

  const [analyticsCalled, setAnalyticsCalled] = useState(false);
  const [analyticsCallSecondarySkus, setAnalyticsCallSecondarySkus] = useState(false);

  const getCustomPriceFilterLabel = useGetCustomPriceFilterLabel();
  const getFilterAnalytics = useGetFilterAnalytics();
  const sessionFiltersList = useGetSessionFiltersList();
  const enable24ProductView = useIs24ProductViewEnabled();
  const storeSelected = useIsStoreSelected();

  const userInteracted = useInteractionCheck();

  const dealBadgeEnabled = dealDrawerShelfEnabled && storeSelected;

  const isBrandPremiumPage = shelfData?.isBrandPremiumPage;
  const shelfBreadcrumb = shelfData?.shelfBreadcrumb;
  const shelfHead = shelfData?.shelfHead;
  const shelfLeftNav = shelfData?.shelfLeftNav;
  const shelfPageBody = shelfData?.shelfPageBody;
  const shelfResultHeader = shelfData?.shelfResultHeader;
  const shelfResultBottom = shelfData?.shelfResultBottom;
  const shelfTippyTop = shelfData?.shelfTippyTop;
  const makeOrModelName = shelfData?.makeorModelName;

  const parts = shelfData?.shelfParts ?? [];
  const prevParts = usePrevious(parts);

  const { data: ymmeData } = useYmmeDefaultData();
  const defaultEngine = ymmeData.defaultEngine;

  const {
    pageSpecificData: {
      pfmLocation,
      searchOriginalPartTypeId,
      searchProductRepositoryId,
      searchResultPosition,
      productFindingMethod,
      ignoreNextPageViewCall,
      eventType: eventTypeAnalytics,
    },
  } = usePageSpecificData();

  const productStrikeThroughPricing: string[] = [];
  const analyticsParts = useMemo(() => {
    if (!shelfData?.shelfParts) return [];
    return enable24ProductView && !isLoadMoreEnabled
      ? analyticsCallSecondarySkus
        ? shelfData.shelfParts.slice(12)
        : shelfData.shelfParts.slice(0, 12)
      : isLoadMoreEnabled && loadMoreClicked
      ? shelfData.shelfParts.slice(-24)
      : shelfData.shelfParts;
  }, [
    shelfData?.shelfParts,
    enable24ProductView,
    isLoadMoreEnabled,
    analyticsCallSecondarySkus,
    loadMoreClicked,
  ]);

  const skuView = analyticsParts.map(() => '1');
  const productBestSellerBadge: string[] = analyticsParts.map((part) => {
    return part.bestSeller ? 'yes' : 'no';
  });

  analyticsParts.forEach((part) => {
    const strikethroughProduct = getProductStrikethroughAnalyticValue(
      part.skuPricingAndAvailability,
      part.skuPricingAndAvailability?.discountedPrice,
      strikeThroughPricingEnabled
    );
    productStrikeThroughPricing.push(strikethroughProduct[0]);
  });

  const pageRecData = useGetShelfPageRecords(shelfPageBody);
  const parsedPath = getPathnameFromAsPath(router.asPath);

  const productFinderData = !isACESEnabled
    ? shelfData?.productFinderData ??
      (productShelfResultsData?.productShelfResults as ProductShelfResultsModel)
        ?.vehicleProductAttributes
    : undefined;

  const searchRedirectText =
    typeof router.query.searchText === 'string' ? router.query.searchText : '';

  const totalRecs =
    searchRedirectText && isBrandShelfPage(parsedPath)
      ? brandShelfData?.records?.length
      : pageRecData?.totalNumRecs ?? 0;

  const partTypeFilterAnalytic = isBrandShelf
    ? brandShelfData?.staticNavigation
        ?.find((filter) => filter.dimensionName === 'part_type_name_s')
        ?.refinements.map((refinement) => `${refinement.label} (${refinement.count})`)
        .join(', ')
    : `${productShelfResultsData?.productShelfResults?.pageDisplayName} (${productShelfResultsData?.productShelfResults?.totalNumberOfRecords})`;

  const prevVehicleId = usePrevious(preferredVehicle?.catalogVehicleId);
  const prevAsPath = usePrevious(asPath);
  const prevStoreNumber = usePrevious(storeDetails?.storeNumber);

  const [sortingOptions = []] = useFilteredSortingOptions() || [];
  const showSortOptions = !!sortingOptions.length;
  const selectedsortValue = getSelectedSortingOptionLabel(
    query.sort,
    sortingOptions,
    labels.featuredSortingOption
  );

  const shelfAnalyticsData = getShelfAnalytics({
    isList,
    parts: analyticsParts,
    shelfBreadcrumb,
    shelfPageBody,
    brandPageBody: brandShelfData,
    shelfCatLabel,
    shelfLeftNav,
    selectedNullCartridge,
    headerData,
    query,
    getCustomPriceFilterLabel,
    getFilterAnalytics,
    sessionFiltersList,
    makeOrModelName,
    defaultEngine,
    dealBadgeEnabled,
    enableVdpDsUniqueDisplay,
    acesEnabled: isACESEnabled,
  });

  const { data: reviewStatistics } = useReviewStatistics({
    skuNumbers: shelfAnalyticsData.productIdList,
    enabled: true,
  });

  const { productAverageRating, productReviewCount } = formatReviewStatistics(
    reviewStatistics,
    shelfAnalyticsData.productIdList
  );

  // MARK: unmount

  useEffect(() => {
    return () => {
      setPageSpecificData({
        eventType: '',
      });
      dispatchRemoveDataFromPage();
      resetAnalyticsDatalayer(shelfPageVariables);
      dispatch(setSortByOrItemsPerPageFocus(false, false));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // MARK: change vehicle, store, or URL

  useEffect(() => {
    if (
      prevVehicleId !== preferredVehicle?.catalogVehicleId ||
      prevAsPath !== asPath ||
      storeDetails?.storeNumber !== prevStoreNumber
    ) {
      setAnalyticsCalled(false);
      setAnalyticsCallSecondarySkus(false);
    }
  }, [
    asPath,
    preferredVehicle?.catalogVehicleId,
    prevAsPath,
    prevStoreNumber,
    prevVehicleId,
    storeDetails?.storeNumber,
  ]);

  // MARK: load more

  useEffect(() => {
    if (
      (isBrandShelfDataFetching ||
        shelfRequestFetching ||
        additionalProductSkuDetailsIsFetching ||
        productSkuIsFetching ||
        infiniteQueryIsPending) &&
      analyticsCalled &&
      loadMoreClicked &&
      isLoadMoreEnabled
    ) {
      setAnalyticsCalled(false);
    }
  }, [
    loadMoreClicked,
    analyticsCalled,
    shelfRequestFetching,
    isBrandShelfDataFetching,
    additionalProductSkuDetailsIsFetching,
    infiniteQueryIsPending,
    isLoadMoreEnabled,
    productSkuIsFetching,
  ]);

  // MARK: secondary SKUs

  useEffect(() => {
    if (prevSecondaryProductSkuIsFetching && !secondaryProductSkuIsFetching && analyticsCalled) {
      setAnalyticsCalled(false);
      setAnalyticsCallSecondarySkus(true);
    }
  }, [
    secondaryProductSkuIsFetching,
    prevSecondaryProductSkuIsFetching,
    setAnalyticsCalled,
    analyticsCalled,
  ]);

  // MARK: analytics call

  useEffect(() => {
    const parsedPath = getPathnameFromAsPath(router.asPath);
    const isBrandsPage = isBrandShelfPage(parsedPath);
    if (
      isUtagDefined &&
      shelfRequestSuccess &&
      !productSkuIsFetching &&
      !secondaryProductSkuIsFetching &&
      !analyticsCalled &&
      !shelfRequestFetching &&
      !isBrandShelfDataFetching &&
      !ignoreNextPageViewCall &&
      userInteracted &&
      (loadMoreClicked
        ? !additionalProductSkuDetailsIsFetching &&
          !infiniteQueryIsPending &&
          parts.length !== prevParts?.length
        : true)
    ) {
      const pageName = isBrandsPage
        ? typeof router.query.brandName === 'string'
          ? router.query.brandName
          : router.query.brandName?.join()
        : analyticsDynamicPageName(shelfBreadcrumb, 'shelf');
      const leafCategoryName = shelfBreadcrumb?.slice(-1)[0]?.label;
      const pfmLocationAnalytics =
        pfmLocation &&
        eventTypeAnalytics !== eventTypes.productFitmentBanner &&
        eventTypeAnalytics !== eventTypes.productToastNotification
          ? `${pfmLocation}: ${leafCategoryName ?? ''}`
          : pfmLocation
          ? pfmLocation
          : '';
      const productFinderAnalytics = getProductFinderAnalytics(productFinderData);
      const segmentArrayCount = asPath.split('/').length;
      const hasTrailingSlash = asPath.endsWith('/');
      const segmentCount = segmentArrayCount - 1 - (hasTrailingSlash ? 1 : 0);
      let analyticsPageType:
        | 'catalog_shelf'
        | 'faceted_nav'
        | 'make_part_type'
        | 'Brand Profile'
        | 'Brand Profile Premium'
        | 'model_part_type'
        | 'year_make_model_part_type' = pageTypes.productShelf;
      const isFacetedUrl = FACETED_URL.test(parsedPath);
      if (isFacetedUrl) {
        analyticsPageType = pageTypes.facetedNav;
      } else if (isBrandsPage) {
        analyticsPageType = isBrandPremiumPage ? pageTypes.brandShelfPremium : pageTypes.brandShelf;
      } else {
        switch (segmentCount) {
          case 3:
            analyticsPageType = pageTypes.makePartType;
            break;

          case 4:
            analyticsPageType = pageTypes.modelPartType;
            break;

          case 5:
            analyticsPageType = pageTypes.yearMakeModelPartType;
            break;

          default:
            break;
        }
      }

      if (root.location.href !== root.utag_data?.pageURL) {
        //if coming in from category page, useAnalytics gets called before the url changes since shelf and category share the same general route
        //we need to re-setup analytics so it has the correct pageURL in this scenario
        setupAnalytics(false, router.asPath, isUtagDefined);
      }

      const removeUnusedAnalyticsData = ['rewardPoints'];
      let pageType: 'catalog_shelf' | 'Brand Profile Premium' | 'Brand Profile' =
        pageTypes.productShelf;

      if (isBrandsPage) {
        pageType = isBrandPremiumPage ? pageTypes.brandShelfPremium : pageTypes.brandShelf;
      }

      const isUnitOfMeasureMatch = !isACESEnabled
        ? (productShelfResultsData?.productShelfResults as ProductShelfResultsModel)
            ?.isUnitOfMeasureMatch
        : (productShelfResultsData?.productShelfResults as ProductShelfACESResultsModel)
            ?.unitOfMeasureMatch;

      const hasFromStringSearchParam = query.fromString && query.fromString === 'search';
      const visualCategoryRank = sessionStorage.getItem('visualCategoryRank');
      const productFindingMethodFromUtag =
        typeof root.utag_data?.productFindingMethod === 'string'
          ? root.utag_data.productFindingMethod
          : '';

      const utagData = {
        pageName: getPageName(pageType, pageName ?? ''),
        siteSection: siteSections.siteSectionProductShelf,
        pageType: isBrandsPage ? pageTypes.brandShelf : pageTypes.productShelf,
        pageHierarchy: `Home|${
          pageName?.includes('-')
            ? pageName
                .replace(/-/g, ' ')
                .replace(/:/g, '|')
                .replace(/\b([a-z])/g, (x) => x.toUpperCase())
            : pageName?.replace(/\b([a-z])/g, (x) => x.toUpperCase()) ?? ''
        }`,
        searchType:
          searchProductData?.isUnitOfMeasureMatch || isUnitOfMeasureMatch
            ? 'UnitOfMeasure'
            : undefined,
        contentBlockNames: [
          ...getContentBlockNames(shelfTippyTop),
          ...getContentBlockNames(shelfResultHeader),
          ...getContentBlockNames(shelfResultBottom),
          ...getContentBlockNames(shelfPageBody),
          ...getContentBlockNames(shelfLeftNav),
          ...getContentBlockNames(shelfHead),
        ],
        contentBlockTypes: [
          ...getContentBlockTypes(shelfTippyTop),
          ...getContentBlockTypes(shelfResultHeader),
          ...getContentBlockTypes(shelfResultBottom),
          ...getContentBlockTypes(shelfPageBody),
          ...getContentBlockTypes(shelfLeftNav),
          ...getContentBlockTypes(shelfHead),
        ],
        seoPageType: getSeoPageType(analyticsPageType, router.asPath),
        seoPopularSearches: !!document.getElementById('#popular-searches-container'),
        seoPopularSearchesClick: sessionStorage.getItem('popularSearchClicked') === 'true',
        selectedsortValue: showSortOptions ? selectedsortValue : null,
        ...productFinderAnalytics,
        searchOriginalPartTypeId,
        searchProductRepositoryId,
        searchResultPosition,
        ...shelfAnalyticsData,
        eventType:
          eventType ||
          (loadMoreClicked
            ? 'loadMore'
            : searchRedirectText && !!totalRecs
            ? 'successfulSearch'
            : searchRedirectText && totalRecs === 0
            ? 'nullSearch'
            : undefined),
        selectedFilterValue: selectedFilterValue,
        ...(searchRedirectText
          ? {
              searchKeyword: searchRedirectText,
            }
          : hasFromStringSearchParam && !!query.filterByKeyWord
          ? {
              searchKeyword: query.filterByKeyWord,
            }
          : {}),
        searchMethod: sessionStorage.getItem('searchMethod') || '',
        prodView: eventTypes.prodView,
        skuView: skuView,
        productFindingMethod: isNavigatedFromVisualFilter
          ? 'Visual Part Type Search'
          : productFindingMethod || productFindingMethodFromUtag,
        productFitmentBanner:
          eventTypeAnalytics === eventTypes.productFitmentBanner ? eventTypeAnalytics : '',
        productToastNotification:
          eventTypeAnalytics === eventTypes.productToastNotification ? eventTypeAnalytics : '',
        pfmLocation:
          isNavigatedFromVisualFilter && typeof visualCategoryRank === 'string'
            ? `Visual Category Rank ${visualCategoryRank}`
            : pfmLocationAnalytics,
        productStrikeThroughPricing: productStrikeThroughPricing,
        pageLazyLoad: enable24ProductView && analyticsCallSecondarySkus,
        partTypeFiltersAvailable: partTypeFilterAnalytic,
        productBestSellerBadge: productBestSellerBadge,
        productAverageRating,
        productReviewCount,
        ...(isVisualFiltersDisplayedOnShelf && {
          productFilterSelection: 'Visual Filter: Composition: Synthetic',
          productFindingMethod: 'Visual Part Type Search',
        }),
      };
      pageViewCall(utagData, [
        ...removeUnusedAnalyticsData,
        ...getAnalyticsRelativeComplementOf(utagData, undefined),
      ]);
      dispatchClearProductFilterEvents();
      setAnalyticsCalled(true);
      sessionStorage.removeItem('searchMethod');
      sessionStorage.setItem('performedSearch', 'false');
      //clear search variables after pageViewCall
      setPageSpecificData({
        searchOriginalPartTypeId: '',
        searchProductRepositoryId: '',
        searchResultPosition: '',
        productFindingMethod: '',
        pfmLocation: '',
      });
      sessionStorage.setItem('popularSearchClicked', 'false');
      if (selectedNullCartridge) {
        dispatchSetSelectedNullCartridge('');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isUtagDefined,
    shelfRequestSuccess,
    shelfRequestFetching,
    isBrandShelfDataFetching,
    productSkuIsFetching,
    secondaryProductSkuIsFetching,
    asPath,
    shelfData,
    shelfData?.shelfParts,
    preferredVehicle?.catalogVehicleId,
    userInteracted,
    analyticsCalled,
    analyticsParts,
    additionalProductSkuDetailsIsFetching,
    infiniteQueryIsPending,
    loadMoreClicked,
  ]);
};
